import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Container, Typography, Box, Alert, TextField,
  Button, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';

const DEBOUNCE_MS = 500;

const ACTION_COLORS = {
  subscription_created: 'success',
  subscription_canceled: 'error',
  subscription_upgrade: 'info',
  trial_started: 'info',
  trial_converted: 'success',
  trial_expired: 'warning',
  payment_refunded: 'warning',
  admin_override_granted: 'secondary',
  admin_override_expired: 'default',
  product_created: 'primary',
};

function DetailDialog({ open, onClose, details }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Entry Details</DialogTitle>
      <DialogContent>
        <pre style={{
          fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          background: '#f5f5f5', padding: 12, borderRadius: 6, maxHeight: 400, overflow: 'auto',
          fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace"
        }}>
          {JSON.stringify(details, null, 2)}
        </pre>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const [searchInput, setSearchInput] = useState('');

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState(null);

  // Load data — fetch all rows for client-side table
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', '1000');
      if (search) params.set('search', search);

      const data = await billingApi.get(`/api/admin/audit-log?${params.toString()}`);
      setRows((data.entries || []).map(e => ({
        ...e,
        details: typeof e.details === 'string' ? JSON.parse(e.details) : e.details,
      })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search
  function handleSearchInput(value) {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(value);
    }, DEBOUNCE_MS);
  }

  function exportCsv() {
    const headers = ['Time', 'Actor Type', 'Actor ID', 'Action', 'Entity Type', 'Entity ID', 'Details', 'IP'];
    const csvRows = rows.map(r => [
      r.created_at, r.actor_type, r.actor_id || '', r.action,
      r.entity_type || '', r.entity_id || '',
      r.details ? JSON.stringify(r.details).replace(/"/g, '""') : '',
      r.ip_address || ''
    ].map(v => `"${v}"`).join(','));

    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const columns = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Time',
      width: 170,
      type: 'date',
      renderCell: (row) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
          {formatDate(row.created_at)}
        </Typography>
      ),
    },
    {
      field: 'actor_type',
      headerName: 'Actor',
      width: 130,
      type: 'singleSelect',
      filterOptions: ['system', 'admin', 'user', 'webhook', 'scheduler'],
      renderCell: (row) => (
        <Box>
          <Chip label={row.actor_type} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          {row.actor_id && (
            <Typography variant="caption" sx={{ display: 'block', fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.65rem', color: 'text.secondary' }}>
              {row.actor_id.slice(0, 12)}...
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 220,
      type: 'singleSelect',
      filterOptions: Object.keys(ACTION_COLORS),
      renderCell: (row) => (
        <Chip
          label={row.action?.replace(/_/g, ' ')}
          size="small"
          color={ACTION_COLORS[row.action] || 'default'}
          variant="outlined"
          sx={{ fontSize: '0.75rem', fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'entity_type',
      headerName: 'Entity',
      width: 180,
      type: 'singleSelect',
      filterOptions: ['subscription', 'payment', 'customer', 'product', 'plan', 'override', 'discount', 'tax_rate', 'tax_exemption'],
      renderCell: (row) => {
        if (!row.entity_type) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{row.entity_type}</Typography>
            {row.entity_id && (
              <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.65rem', color: 'text.secondary' }}>
                {row.entity_id.slice(0, 12)}...
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 1,
      minWidth: 350,
      filterable: false,
      renderCell: (row) => {
        if (!row.details) return <Typography variant="body2" color="text.secondary">—</Typography>;
        const text = JSON.stringify(row.details);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
            <Typography variant="body2" sx={{
              fontSize: '0.75rem', fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
            }}>
              {text}
            </Typography>
            <Tooltip title="View full details">
              <IconButton size="small" onClick={() => setDetailDialog(row.details)} aria-label="View details">
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'ip_address',
      headerName: 'IP',
      width: 120,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
          {row.ip_address || '—'}
        </Typography>
      ),
    },
  ], []);

  return (
    <Container maxWidth={false} sx={{ py: 3, px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Audit Log</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV
          </Button>
          <IconButton size="small" onClick={loadData} title="Refresh" aria-label="Refresh audit log">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search across all fields..."
          value={searchInput}
          onChange={(e) => handleSearchInput(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
          }}
        />
        {searchInput && (
          <Button size="small" startIcon={<ClearIcon />} onClick={() => { setSearchInput(''); setSearch(''); }}>
            Clear
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {rows.length.toLocaleString()} entries
        </Typography>
      </Box>

      {/* Table */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%' }}>
        <AdminTable
          rows={rows}
          columns={columns}
          defaultSort={[{ field: 'created_at', direction: 'desc' }]}
          defaultPageSize={25}
          loading={loading}
        />
      </Box>

      {/* Detail Dialog */}
      <DetailDialog
        open={!!detailDialog}
        onClose={() => setDetailDialog(null)}
        details={detailDialog}
      />
    </Container>
  );
}
